import Promise from 'bluebird';
import { useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  loadAdGroup,
  modelToViewModelForAdGroup,
  modelToViewModelForCampaign,
  loadBundleCountries,
  readAudienceCriterions,
  readTargetingCriterions,
  readLocationCriterions,
  saveCampaign,
  saveJustAdGroup,
  readDayTimeCriterions,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { readDayTimeCriterions as readDayTimeCriterionsCampaign } from '../../shared/model/campaign-criterion';
import { isAudienceCampaign, isDealCampaign } from '../../step-constants';

export const useSummaryRepository = () => {
  const i18n = useContext(I18nContext);
  const { state } = useWizardService();

  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);

  const scenarioContext = useContext(ScenarioContext);

  const { cid, aid } = scope.values;

  const { flow, isPublishing, deal } = state.context.wizardData;

  const inMarketAudienceRedesignEnabled = _.get(serverContext, ['DynamicPermissions', 'InMarketAudienceRedesign']);

  const load = () => {
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null;

    let loadPromises = {
      adGroup: Promise.resolve(savedAdGroup),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
    };

    loadPromises = _.extend(loadPromises, {
      adGroup: loadAdGroup(cid, aid, campaignId, adGroupId),
      campaign: loadCampaign(cid, aid, campaignId),
      locationCriterions: readLocationCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      dayTimeCriterions: readDayTimeCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      dayTimeCriterionsCampaign: readDayTimeCriterionsCampaign({
        cid, aid, campaignId,
      }),
      targetCriterions: readTargetingCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      audienceCriterions: readAudienceCriterions({
        cid, aid, campaignId, adGroupId,
      }),
    });

    return Promise.props(loadPromises).then(({
      adGroup,
      campaign,
      locationCriterions,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
      dayTimeCriterionsCampaign,
      targetCriterions,
      audienceCriterions,
    }) => {
      const adGroupData = modelToViewModelForAdGroup({
        adGroupModel: adGroup,
        campaignModel: campaign,
        locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        targetCriterionsModel: targetCriterions ? targetCriterions.value : [],
        audienceCriterionsModel: audienceCriterions ? audienceCriterions.value : [],
        i18n,
        isAudienceAds: isAudienceCampaign(flow),
        separateIMA: inMarketAudienceRedesignEnabled,
      });

      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        dayTimeCriterionsModel: dayTimeCriterionsCampaign ? dayTimeCriterionsCampaign.value : [],
      });

      return {
        ads: state.context.wizardData && state.context.wizardData.ads,
        adGroup: adGroupData.data,
        campaign: campaignData.data,
        ...(isDealCampaign(flow) ? { deal } : {}),
      };
    });
  };

  const save = ({ data }) => {
    const savedCampaign = data.campaign;
    const savedAdGroup = data.adGroup;

    if (isPublishing) {
      savedCampaign.Status = 'Active';
      savedAdGroup.Status = 'Active';
    } else {
      savedCampaign.Status = 'Paused';
    }

    return new Promise((resolve, reject) => saveCampaign({
      campaign: { data: savedCampaign },
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    }).then(() => saveJustAdGroup({
      adGroup: { data: savedAdGroup },
      campaign: savedCampaign,
    }, {
      i18n,
    })).then(() => {
      resolve();
    }).catch(err => reject(err)));
  };

  return {
    load,
    save,
  };
};
